import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import { 
  FiMail, FiClock, FiShield, FiHeart, 
  FiChevronDown, FiChevronUp 
} from 'react-icons/fi';

const ContactPage = () => {
  const { t } = useLanguage();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const heroRef = useRef(null);
  const companyRef = useRef(null);
  const emailRef = useRef(null);
  const featuresRef = useRef(null);
  const faqRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const companyInView = useInView(companyRef, { once: true, margin: "-100px" });
  const emailInView = useInView(emailRef, { once: true, margin: "-100px" });
  const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const faqInView = useInView(faqRef, { once: true, margin: "-100px" });

  const faqs = [
    { question: t('pages.faq1Question'), answer: t('pages.faq1Answer') },
    { question: t('pages.faq2Question'), answer: t('pages.faq2Answer') },
    { question: t('pages.faq3Question'), answer: t('pages.faq3Answer') },
    { question: t('pages.faq4Question'), answer: t('pages.faq4Answer') },
  ];

  // FAQ Structured Data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEO 
        title={t('common.seo.contact.title')}
        description={t('common.seo.contact.description')}
        keywords={t('common.seo.contact.keywords')}
        structuredData={faqStructuredData}
      />
      <div className="pt-20 sm:pt-24 pb-16">
        {/* Hero Section */}
        <section ref={heroRef} className="bg-gradient-to-br from-primary-600 to-secondary-700 pt-16 pb-20 px-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 paw-pattern-bg opacity-10"></div>
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div 
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7 }}
            >
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <SafeIcon icon={FiMail} className="text-3xl" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('pages.contactUs')}</h1>
              <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
                {t('pages.contactUsSubtitle')}
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 50" className="fill-white">
              <path d="M0,25L80,28.3C160,32,320,38,480,35C640,32,800,20,960,17C1120,13,1280,20,1360,23.3L1440,25L1440,50L1360,50C1280,50,1120,50,960,50C800,50,640,50,480,50C320,50,160,50,80,50L0,50Z"></path>
            </svg>
          </div>
        </section>

        {/* Company Introduction */}
        <section ref={companyRef} className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={companyInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-4 shadow-sm">
                {t('pages.technicalSupport')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('pages.homyTechLimited')}</h2>
              <p className="text-neutral-600 text-lg max-w-2xl mx-auto">
                {t('pages.homyTechDescription')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Email Contact Card */}
        <section ref={emailRef} className="py-16 px-6 bg-gradient-to-br from-neutral-50 to-primary-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white rotate-180">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>

          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={emailInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6 }}
            >
              <a 
                href="mailto:info@homy.hk"
                className="block bg-gradient-to-br from-primary-500 to-secondary-600 rounded-2xl shadow-2xl p-12 text-center text-white hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
                aria-label="Send email to Pet Care Note support at info@homy.hk"
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="text-4xl" />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">{t('pages.contactByEmail')}</h3>
                <p className="text-xl md:text-2xl font-semibold mb-4 text-blue-50">info@homy.hk</p>
                <p className="text-blue-100 max-w-xl mx-auto">{t('pages.clickToSendEmail')}</p>
              </a>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>
        </section>

        {/* Service Features */}
        <section ref={featuresRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={featuresInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FeatureCard 
                icon={FiClock} 
                title={t('pages.responseTime')} 
                description={t('pages.responseTimeDesc')} 
                color="text-primary-600"
                bgColor="bg-primary-100"
              />
              <FeatureCard 
                icon={FiShield} 
                title={t('pages.privacyProtection')} 
                description={t('pages.privacyProtectionDesc')} 
                color="text-secondary-600"
                bgColor="bg-secondary-100"
              />
              <FeatureCard 
                icon={FiHeart} 
                title={t('pages.carefulReply')} 
                description={t('pages.carefulReplyDesc')} 
                color="text-accent-600"
                bgColor="bg-accent-100"
              />
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section ref={faqRef} className="py-16 px-6 bg-gradient-to-br from-neutral-50 to-primary-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 35" className="fill-white rotate-180">
              <path d="M0,18L80,20C160,22,320,26,480,24C640,22,800,14,960,12C1120,10,1280,14,1360,16L1440,18L1440,35L1360,35C1280,35,1120,35,960,35C800,35,640,35,480,35C320,35,160,35,80,35L0,35Z"></path>
            </svg>
          </div>

          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={faqInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-medium mb-4 shadow-sm">
                {t('pages.faq')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('pages.commonQuestions')}</h2>
              <p className="text-neutral-600 text-lg">{t('pages.faqSubtitle')}</p>
            </motion.div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={faqInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl shadow-smooth border border-neutral-100 overflow-hidden"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors"
                    aria-expanded={expandedFaq === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="font-semibold text-neutral-800 pr-4" itemProp="name">{faq.question}</span>
                    <SafeIcon 
                      icon={expandedFaq === index ? FiChevronUp : FiChevronDown} 
                      className="text-xl text-neutral-400 flex-shrink-0"
                      aria-hidden="true"
                    />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === index && (
                      <motion.div
                        id={`faq-answer-${index}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                      >
                        <div className="px-6 pb-5 text-neutral-600 border-t border-neutral-100 pt-4" itemProp="text">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </motion.div>

            {/* More Questions CTA */}
            <motion.div
              className="mt-12 text-center bg-white rounded-xl shadow-smooth border border-neutral-100 p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={faqInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-xl font-bold mb-3 text-neutral-800">{t('pages.moreQuestions')}</h3>
              <p className="text-neutral-600 mb-6">{t('pages.moreQuestionsDesc')}</p>
              <a 
                href="mailto:info@homy.hk"
                className="inline-flex items-center gap-2 btn btn-primary"
                aria-label="Contact Pet Care Note support via email"
              >
                <SafeIcon icon={FiMail} />
                {t('pages.sendEmailNow')}
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

const FeatureCard = ({ icon, title, description, color, bgColor }) => (
  <motion.div
    className="bg-white rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 p-6 text-center"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
  >
    <div className={`w-16 h-16 ${bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
      <SafeIcon icon={icon} className={`text-2xl ${color}`} />
    </div>
    <h3 className="text-xl font-semibold mb-3 text-neutral-800">{title}</h3>
    <p className="text-neutral-600">{description}</p>
  </motion.div>
);

export default ContactPage;